import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function bufToB64(buf) {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return window.btoa(s);
}

export function usePush() {
  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [permission, setPermission] = useState(
    supported ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [serverConfigured, setServerConfigured] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      setSubscribed(!!existing);
    } catch (e) {
      // ignore
    }
  }, [supported]);

  useEffect(() => {
    refresh();
    api
      .get("/push/vapid-public-key")
      .then((r) => setServerConfigured(!!r.data.configured))
      .catch(() => setServerConfigured(false));
  }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!supported) {
      setError("Push not supported on this browser/device");
      return false;
    }
    setWorking(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setError("Permission denied");
        return false;
      }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.public_key) {
        setError("Server VAPID key missing");
        return false;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(data.public_key),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: {
          p256dh: json.keys?.p256dh || bufToB64(sub.getKey("p256dh")),
          auth: json.keys?.auth || bufToB64(sub.getKey("auth")),
        },
        label: navigator.userAgent.slice(0, 80),
      });
      setSubscribed(true);
      return true;
    } catch (e) {
      setError(e?.message || "Subscribe failed");
      return false;
    } finally {
      setWorking(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setWorking(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api
          .post("/push/unsubscribe", { endpoint: sub.endpoint })
          .catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setWorking(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/push/test");
      return data;
    } finally {
      setWorking(false);
    }
  }, []);

  return {
    supported,
    serverConfigured,
    permission,
    subscribed,
    working,
    error,
    subscribe,
    unsubscribe,
    sendTest,
    refresh,
  };
}
